import { parseDateOnly } from "@nimit9/signet-lib/format"
import { AnswerHeadline } from "@nimit9/signet-ui/components/answer-headline"
import { Button } from "@nimit9/signet-ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@nimit9/signet-ui/components/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@nimit9/signet-ui/components/drawer"
import { EmptyState } from "@nimit9/signet-ui/components/empty-state"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { Input } from "@nimit9/signet-ui/components/input"
import { Label } from "@nimit9/signet-ui/components/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nimit9/signet-ui/components/select"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nimit9/signet-ui/components/table"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useId, useMemo, useState } from "react"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { useDateFormat } from "@/hooks/use-date-format"
import { type InsurancePolicy, useInsurance } from "@/hooks/use-insurance"
import { useOwnerLabels } from "@/hooks/use-owner-labels"
import { renderAnswerAmount, toSignetHeadline } from "@/lib/answer-headline"
import { api } from "@/lib/api"
import { cn } from "@/lib/cn"
import { daysUntil } from "@/lib/dates"
import { annualPremium, buildInsuranceHeadline, isActivePolicy } from "./-headline"

export const Route = createFileRoute("/insurance/")({
  component: Insurance,
})

const TYPE_ICONS: Record<string, string> = {
  term: "🛡️",
  health: "🏥",
  vehicle: "🚗",
  home: "🏠",
  other: "📋",
}

const TYPE_ITEMS: Record<string, string> = {
  term: "Term",
  health: "Health",
  vehicle: "Vehicle",
  home: "Home",
  other: "Other",
}

const FREQUENCY_ITEMS: Record<string, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half-yearly",
  yearly: "Yearly",
}

/** A renewal inside this many days (or already past) is flagged amber. */
const EXPIRING_DAYS = 30
const UPCOMING_DAYS = 90

/** Add a policy, or edit `policy` when given (UAT J6-09: rows only offered Delete). */
function AddPolicyForm({ onClose, policy }: { onClose: () => void; policy?: InsurancePolicy }) {
  const id = useId()
  const ownerLabels = useOwnerLabels()
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: policy?.name ?? "",
    type: (policy?.type ?? "health") as string,
    provider: policy?.provider ?? "",
    coverAmount: policy?.coverAmount ?? "",
    premiumAmount: policy?.premiumAmount ?? "",
    premiumFrequency: policy?.premiumFrequency ?? "yearly",
    startDate: policy?.startDate ?? "",
    renewalDate: policy?.renewalDate ?? "",
    owner: policy?.owner ?? "user_1",
    policyNumber: policy?.policyNumber ?? "",
    notes: policy?.notes ?? "",
  })
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      policy
        ? api.patch(`/goals/insurance/${policy.id}`, body)
        : api.post("/goals/insurance", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] })
      onClose()
    },
  })

  const field = (key: keyof typeof form) => `${id}-${key}`

  return (
    <div data-testid="add-policy-form" className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={field("name")}>Policy name</Label>
          <Input
            id={field("name")}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="HDFC Term Plan"
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={field("provider")}>Provider</Label>
          <Input
            id={field("provider")}
            value={form.provider}
            onChange={(e) => set("provider", e.target.value)}
            placeholder="HDFC Life"
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={field("type")}>Type</Label>
          <Select value={form.type} onValueChange={(v) => v && set("type", v)} items={TYPE_ITEMS}>
            <SelectTrigger id={field("type")} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TYPE_ITEMS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={field("owner")}>Owner</Label>
          <Select
            value={form.owner}
            onValueChange={(v) => v && set("owner", v)}
            items={ownerLabels}
          >
            <SelectTrigger id={field("owner")} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ownerLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={field("coverAmount")}>Cover amount</Label>
          <Input
            id={field("coverAmount")}
            value={form.coverAmount}
            onChange={(e) => set("coverAmount", e.target.value)}
            placeholder="10000000"
            type="number"
            inputMode="decimal"
          />
        </div>
        <div className="grid min-w-0 grid-cols-2 gap-3">
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={field("premiumAmount")}>Premium</Label>
            <Input
              id={field("premiumAmount")}
              value={form.premiumAmount}
              onChange={(e) => set("premiumAmount", e.target.value)}
              placeholder="15000"
              type="number"
              inputMode="decimal"
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={field("premiumFrequency")}>Frequency</Label>
            <Select
              value={form.premiumFrequency}
              onValueChange={(v) => v && set("premiumFrequency", v)}
              items={FREQUENCY_ITEMS}
            >
              <SelectTrigger id={field("premiumFrequency")} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(FREQUENCY_ITEMS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={field("startDate")}>Start date</Label>
          <Input
            id={field("startDate")}
            value={form.startDate}
            onChange={(e) => set("startDate", e.target.value)}
            type="date"
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={field("renewalDate")}>Renewal date</Label>
          <Input
            id={field("renewalDate")}
            value={form.renewalDate}
            onChange={(e) => set("renewalDate", e.target.value)}
            type="date"
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
          <Label htmlFor={field("policyNumber")}>Policy number</Label>
          <Input
            id={field("policyNumber")}
            value={form.policyNumber}
            onChange={(e) => set("policyNumber", e.target.value)}
            placeholder="Optional"
          />
        </div>
      </div>
      {mutation.isError && (
        <p role="alert" className="text-caption text-destructive">
          {(mutation.error as Error)?.message || "Something went wrong"}
        </p>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() =>
            mutation.mutate({
              ...form,
              coverAmount: form.coverAmount || undefined,
              renewalDate: form.renewalDate || undefined,
              policyNumber: form.policyNumber || undefined,
              notes: form.notes || undefined,
            })
          }
          disabled={
            !form.name ||
            !form.provider ||
            !form.premiumAmount ||
            !form.startDate ||
            mutation.isPending
          }
        >
          {mutation.isPending ? "Saving..." : policy ? "Save policy" : "Add policy"}
        </Button>
      </div>
    </div>
  )
}

/** Phone: bottom Drawer; desktop: centred Dialog. Same form either way. */
function AddPolicySheet({
  open,
  onClose,
  isDesktop,
  policy,
}: {
  open: boolean
  onClose: () => void
  isDesktop: boolean
  policy?: InsurancePolicy
}) {
  const title = policy ? "Edit policy" : "Add policy"
  if (!isDesktop) {
    return (
      <Drawer open={open} onOpenChange={(o) => !o && onClose()} showSwipeHandle>
        <DrawerContent>
          <DrawerHeader className="pb-3">
            <DrawerTitle>{title}</DrawerTitle>
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            {open && <AddPolicyForm key={policy?.id ?? "new"} onClose={onClose} policy={policy} />}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {open && <AddPolicyForm key={policy?.id ?? "new"} onClose={onClose} policy={policy} />}
      </DialogContent>
    </Dialog>
  )
}

function renewalInfo(p: InsurancePolicy) {
  const days = p.renewalDate ? daysUntil(p.renewalDate) : null
  return { days, isExpiring: days !== null && days < EXPIRING_DAYS }
}

/** Renewal date + "(overdue)" / "(45d)" — amber when close or past, never red. */
function RenewalDate({ policy }: { policy: InsurancePolicy & { renewalDate: string } }) {
  const formatDate = useDateFormat()
  const { days, isExpiring } = renewalInfo(policy)
  return (
    <span className={cn(isExpiring ? "font-medium text-accent-amber" : "text-text-secondary")}>
      {formatDate(parseDateOnly(policy.renewalDate))}
      {days !== null && days < 0 && <span className="ml-1 text-caption">(overdue)</span>}
      {days !== null && days >= 0 && days <= UPCOMING_DAYS && (
        <span className="ml-1 text-caption">({days}d)</span>
      )}
    </span>
  )
}

function EditButton({ policy, onEdit }: { policy: InsurancePolicy; onEdit: () => void }) {
  return (
    <Button variant="ghost" size="sm" aria-label={`Edit ${policy.name}`} onClick={onEdit}>
      Edit
    </Button>
  )
}

function DeleteButton({ policy, onDelete }: { policy: InsurancePolicy; onDelete: () => void }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={`Delete ${policy.name}`}
      className="text-text-muted hover:text-destructive"
      onClick={() => {
        if (confirm(`Delete "${policy.name}"?`)) onDelete()
      }}
    >
      Delete
    </Button>
  )
}

function PolicyMeta({ policy }: { policy: InsurancePolicy }) {
  const ownerLabels = useOwnerLabels()
  return (
    <>
      {policy.provider} · {policy.type} · {ownerLabels[policy.owner] || policy.owner}
      {policy.policyNumber && ` · #${policy.policyNumber}`}
    </>
  )
}

/** Phone: one stacked card per policy. */
function PolicyCard({
  policy,
  onDelete,
  onEdit,
}: {
  policy: InsurancePolicy
  onDelete: () => void
  onEdit: () => void
}) {
  const formatDate = useDateFormat()
  const { isExpiring } = renewalInfo(policy)
  const fact = "text-label uppercase tracking-wider text-text-hint"
  return (
    <li
      data-testid="policy-card"
      className={cn(
        "flex min-w-0 flex-col gap-3 rounded-xl bg-bg-surface p-4 ring-1",
        isExpiring ? "ring-accent-amber" : "ring-border-subtle",
      )}
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-3">
          <span className="text-subhead">{TYPE_ICONS[policy.type] || "📋"}</span>
          <div className="min-w-0">
            <p className="text-body font-medium text-text-primary">{policy.name}</p>
            <p className="text-caption text-text-muted">
              <PolicyMeta policy={policy} />
            </p>
          </div>
        </div>
        <div className="flex shrink-0">
          <EditButton policy={policy} onEdit={onEdit} />
          <DeleteButton policy={policy} onDelete={onDelete} />
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        {policy.coverAmount && (
          <div className="min-w-0">
            <dt className={fact}>Cover</dt>
            <dd className="text-body font-medium text-text-primary">
              <Amount value={policy.coverAmount} />
            </dd>
          </div>
        )}
        <div className="min-w-0">
          <dt className={fact}>Premium</dt>
          <dd className="text-body font-medium text-text-primary">
            <Amount value={policy.premiumAmount} />
            <span className="text-caption text-text-muted">/{policy.premiumFrequency}</span>
          </dd>
        </div>
        <div className="min-w-0">
          <dt className={fact}>Started</dt>
          <dd className="text-caption text-text-secondary">
            {formatDate(parseDateOnly(policy.startDate))}
          </dd>
        </div>
        {policy.renewalDate && (
          <div className="min-w-0">
            <dt className={fact}>Renewal</dt>
            <dd className="text-caption">
              <RenewalDate policy={{ ...policy, renewalDate: policy.renewalDate }} />
            </dd>
          </div>
        )}
      </dl>
      {policy.notes && <p className="text-caption text-text-muted">{policy.notes}</p>}
    </li>
  )
}

/** Desktop: a real table, one row per policy. */
function PoliciesTable({
  policies,
  onDelete,
  onEdit,
}: {
  policies: InsurancePolicy[]
  onDelete: (id: string) => void
  onEdit: (policy: InsurancePolicy) => void
}) {
  const formatDate = useDateFormat()
  return (
    <Table data-testid="policies-table">
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="pl-4">Policy</TableHead>
          <TableHead className="text-right">Cover</TableHead>
          <TableHead className="text-right">Premium</TableHead>
          <TableHead>Started</TableHead>
          <TableHead>Renewal</TableHead>
          <TableHead className="pr-4 text-right">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {policies.map((p) => (
          <TableRow key={p.id} data-testid="policy-row">
            <TableCell className="max-w-96 pl-4 whitespace-normal">
              <div className="flex min-w-0 items-start gap-2">
                <span className="text-callout">{TYPE_ICONS[p.type] || "📋"}</span>
                <div className="min-w-0">
                  <p className="font-medium text-text-primary">{p.name}</p>
                  <p className="text-caption text-text-muted">
                    <PolicyMeta policy={p} />
                  </p>
                  {p.notes && <p className="text-caption text-text-muted">{p.notes}</p>}
                </div>
              </div>
            </TableCell>
            <TableCell className="text-right font-medium text-text-primary">
              {p.coverAmount ? <Amount value={p.coverAmount} /> : "—"}
            </TableCell>
            <TableCell className="text-right font-medium text-text-primary">
              <Amount value={p.premiumAmount} />
              <span className="text-caption text-text-muted">/{p.premiumFrequency}</span>
            </TableCell>
            <TableCell className="text-text-secondary">
              {formatDate(parseDateOnly(p.startDate))}
            </TableCell>
            <TableCell>
              {p.renewalDate ? <RenewalDate policy={{ ...p, renewalDate: p.renewalDate }} /> : "—"}
            </TableCell>
            <TableCell className="pr-4 text-right">
              <EditButton policy={p} onEdit={() => onEdit(p)} />
              <DeleteButton policy={p} onDelete={() => onDelete(p.id)} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function Insurance() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const queryClient = useQueryClient()
  const { data, isLoading, isError, error, refetch } = useInsurance()
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState<InsurancePolicy | undefined>(undefined)

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/goals/insurance/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["goals"] }),
  })

  const policies = useMemo(() => data?.insurance || [], [data])
  const headline = useMemo(() => buildInsuranceHeadline(policies), [policies])
  const totalAnnualPremium = policies.reduce((s, p) => s + annualPremium(p), 0)
  const upcomingRenewals = policies.filter((p) => {
    if (!p.renewalDate) return false
    const days = daysUntil(p.renewalDate)
    return days >= 0 && days < UPCOMING_DAYS
  }).length

  const addButton = (
    <Button size="sm" onClick={() => setShowAdd(true)}>
      + Add policy
    </Button>
  )

  if (isLoading) {
    return (
      <>
        <Header title="Insurance" showPeriod={false} />
        <PageWrapper>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-28 w-full animate-pulse rounded-xl" />
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full animate-pulse rounded-xl" />
            ))}
          </div>
        </PageWrapper>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Header title="Insurance" showPeriod={false} />
        <PageWrapper>
          <ErrorState message={(error as Error)?.message} onRetry={refetch} />
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <Header title="Insurance" showPeriod={false} />
      <PageWrapper>
        <div
          data-testid="insurance-page"
          data-layout={isDesktop ? "desktop" : "phone"}
          className="flex min-w-0 flex-col gap-4 md:gap-5"
        >
          {headline && (
            <AnswerHeadline
              headline={toSignetHeadline(headline)}
              renderValue={renderAnswerAmount}
            />
          )}

          {policies.length > 0 && (
            <StatStrip
              isDesktop={isDesktop}
              items={[
                {
                  label: "Active policies",
                  value: String(policies.filter(isActivePolicy).length),
                },
                { label: "Annual premium", value: <Amount value={totalAnnualPremium} /> },
                {
                  label: "Upcoming renewals",
                  value: String(upcomingRenewals),
                  meta: "next 90 days",
                },
              ]}
            />
          )}

          {policies.length === 0 ? (
            <EmptyState
              title="No policies tracked"
              description="Add your insurance policies to track renewals and cover adequacy"
              action={addButton}
            />
          ) : (
            <section className="flex min-w-0 flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-body text-text-secondary">{policies.length} policies</p>
                {addButton}
              </div>
              {deleteMutation.isError && (
                <p role="alert" className="text-caption text-destructive">
                  {(deleteMutation.error as Error)?.message || "Failed to delete policy"}
                </p>
              )}
              {isDesktop ? (
                <div className="overflow-x-auto rounded-xl bg-bg-surface ring-1 ring-border-subtle">
                  <PoliciesTable
                    policies={policies}
                    onDelete={(id) => deleteMutation.mutate(id)}
                    onEdit={(p) => {
                      setEditing(p)
                      setShowAdd(true)
                    }}
                  />
                </div>
              ) : (
                <ul className="flex flex-col gap-3">
                  {policies.map((p) => (
                    <PolicyCard
                      key={p.id}
                      policy={p}
                      onDelete={() => deleteMutation.mutate(p.id)}
                      onEdit={() => {
                        setEditing(p)
                        setShowAdd(true)
                      }}
                    />
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>
      </PageWrapper>
      <AddPolicySheet
        open={showAdd}
        onClose={() => {
          setShowAdd(false)
          setEditing(undefined)
        }}
        isDesktop={isDesktop}
        policy={editing}
      />
    </>
  )
}
